import { CartItemList } from '@/models/cart/cart-item'
import * as InconsistentError from '@spa/components/pages/order/model/error-message/inconsistent-error'
import { getPaymentErrorMessage, PaymentErrorReason } from '@spa/components/pages/order/model/error-message/payment-error'
import { extractFrontErrorBodiesFromPurchaseResult, OrderPurchaseResult } from '@spa/services/order'
import _ from 'lodash'

export function createPurchaseErrorMessage(
  purchaseResults: OrderPurchaseResult[],
  cartItemList: CartItemList
): string[] {
  const errorBodies = _.compact(purchaseResults.map((result) => {
    return _.get(result.error, 'response.body', null)
  }))

  if (errorBodies.length === 0) {
    return []
  }

  const errorMessages = _.compact(errorBodies.map((body) => {
    switch (body['reason.code']) {
      // tslint:disable-next-line:no-magic-numbers
      case 16101:
        // tslint:disable-next-line:max-line-length
        // https://everforth.backlog.jp/git/APC/EverforthFramework/blob/release/src/main/scala/com/everforth/everforth/context/Reason.scala
        // l.1563 Sales Stock shortage
        return '在庫の足りない商品があるため、注文を完了できませんでした。'
      // tslint:disable-next-line:no-magic-numbers
      case 15300:
        /**
         * getPaymentErrorMessage の引数の型は、ここで渡している生レスポンスのJSONと型を元に定められている。
         * そのため as しているが（じっさいはanyのまま渡しても問題なく通る）、
         * 生レスポンスJSON型が変化したり、レスポンス用の型を別途定義するようになれば、明示的に型のコンバータを用意してほしい。
         */
        return getPaymentErrorMessage(body['reason.detail'] as PaymentErrorReason)
      default:
        return null
    }
  }))

  const errorMessagesByCauseType: string[] = []
  if (hasErrorOnCauseType(purchaseResults, 'NoItemsError')) {
    errorMessagesByCauseType.push('商品の価格が変更されているか、カートから削除されています。')
  }

  if (
      hasErrorOnCauseType(purchaseResults, 'NotInStoreError') ||
      hasErrorOnCauseType(purchaseResults, 'StockIsEmptyError')
    ) {
    errorMessagesByCauseType.push('在庫の足りない商品があるため、注文を完了できませんでした。')
  }
  if (
    !hasErrorOnCauseType(purchaseResults, 'StockIsEmptyError') &&
    hasErrorOnCauseType(purchaseResults, 'UnitCountExceededError')
  ) {
    errorMessagesByCauseType.push('購入上限数に達している商品があるため、注文を完了できませんでした。')
  }

  if (hasErrorOnCauseType(purchaseResults, 'UnapplicableCouponError')) {
    errorMessagesByCauseType.push('このご注文にはご利用いただけないクーポンが選択されています。<br />ご利用になるクーポンを変更するか、クーポンの選択を解除してください。')
  }

  if (hasErrorOnCauseType(purchaseResults, 'RootFkuNotPublicError')) {
    errorMessagesByCauseType.push('販売を終了した商品があるため、注文を完了できませんでした。')
  }

  if (
      hasErrorOnCauseType(purchaseResults, 'NoveltyItemIncludedInCartError') ||
      hasErrorOnCauseType(purchaseResults, 'NoveltyConstraintError')
    ) {
    errorMessagesByCauseType.push('ご利用いただけないノベルティが含まれているため、注文を完了できませんでした。')
  }

  if (hasErrorOnCauseType(purchaseResults, InconsistentError.errorKind)) {
    errorMessagesByCauseType.push('<br/>カート不整合のため、注文を完了できませんでした。<br/>')

    const errorResponseBodies = extractFrontErrorBodiesFromPurchaseResult(purchaseResults)

    _.flatMap(
      errorResponseBodies,
      e => InconsistentError.buildErrorMessages(e, cartItemList)
    ).forEach((message) => {
      errorMessagesByCauseType.push(`${message}<br/>`)
    })
  }

  return [...errorMessages, ...errorMessagesByCauseType]
}

export function hasErrorOnCauseType(results: OrderPurchaseResult[], errorType: string): boolean {
  const errorBodies = extractFrontErrorBodiesFromPurchaseResult(results)

  if (errorBodies.length === 0) {
    return false
  }

  const errorBodiesCauseTypes = _.compact(
    errorBodies.map(body =>
      _.map(body.cause, cause => (cause.match(/\[(.+?)\]/) || [])[1])
    )
  )

  return _.some(errorBodiesCauseTypes, errorCauseTypes => _.includes(errorCauseTypes, errorType))
}
